import React, { useState, useRef } from "react";
import {
  Box,
  Typography,
  Stack,
  Grid,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import UpcomingSessionCard from "./UpcomingSession/UpCommingSesionCard";
import GroupedStageBookingsCarousel from "./RecentCardCarousel/BookedStageModeCarousel";
import { useNavigate } from "react-router-dom";
import PerfomanceSnapshots from "../TalentDashboard/PerfomanceSnaps/PerfomanceSnapshots";
import {
  mockSnapshotData,
} from "../TalentDashboard/DummyData/DummyData";
import ChipWalletOverview from "../TalentDashboard/ChipWallet/ChipWalletOverview";
import SuitSelectorWithSection from "../Talent/Card/UserCardDesign/SuitSelector_favorite";
import UserSelectedDecks from "../Talent/Card/UserCardDesign/UserSelectedDecks";
import SectionTabsUser from "./SectionTabUser";
import GuestLiteModal from "../../../pages/Auth/GuestLiteModal";
import { GradientButton } from "../../../pages/Auth/SignInDesign";
import { useAuth } from "../../../hooks/useAuth";

const UserDashboard_updated = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const analyticsRef = useRef<HTMLDivElement>(null);
  const bookingsRef = useRef<HTMLDivElement>(null);
  const upcomingRef = useRef<HTMLDivElement>(null);
  const walletRef = useRef<HTMLDivElement>(null);
  const favoriteRef = useRef<HTMLDivElement>(null);
  const myDeckRef = useRef<HTMLDivElement>(null);

  const [activeTab, setActiveTab] = useState("analytics");

  const { userInfo } = useAuth();

  const isGuestLite = userInfo?.profile_completed === false;
  const [isGuestLiteModalOpen, setIsGuestLiteModalOpen] = useState(false);

  const handleTabChange = (
    _: React.SyntheticEvent,
    newValue: string | number
  ) => {
    if (typeof newValue === "string") {
      setActiveTab(newValue);
      const targetSection = document.getElementById(newValue);
      if (targetSection) {
        targetSection.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  };

  // —— Netflix-ish tokens (design only)
  const COLORS = {
    bg: "#0b0b0b",
    panel: "#141414",
    border: "rgba(255,255,255,0.12)",
    text: "#ffffff",
    muted: "rgba(255,255,255,0.72)",
    red: "#E50914",
    redHover: "#b20710",
  };

  const sectionCardSx = {
    scrollMarginTop: 80,
    border: `1px solid ${COLORS.border}`,
    borderRadius: 3,
    px: { xs: 2, md: 3 },
    py: { xs: 3, md: 4 },
    mb: 5,
    background:
      "linear-gradient(180deg, rgba(20,20,20,0.96), rgba(18,18,18,0.96))",
    color: COLORS.text,
    boxShadow: "0 18px 48px rgba(0,0,0,0.6)",
    position: "relative" as const,
    overflow: "hidden",
    "&:before": {
      content: '""',
      position: "absolute",
      inset: 0,
      background:
        "radial-gradient(800px 320px at 10% 0%, rgba(229,9,20,0.08), transparent 60%)",
      pointerEvents: "none",
    },
  };

  return (
    <Box
      px={{ xs: 2, md: 4 }}
      py={isMobile ? 2 : 4}
      sx={{
        minHeight: "100vh",
        backgroundColor: COLORS.bg,
        position: "relative",
        "&:after": {
          content: '""',
          position: "fixed",
          inset: 0,
          background:
            "radial-gradient(1200px 600px at 90% -10%, rgba(255,255,255,0.05), transparent 60%)",
          pointerEvents: "none",
          mixBlendMode: "screen",
        },
      }}
    >
      {/* Sticky Header */}
      <Box
        sx={{
          position: "sticky",
          top: 50,
          zIndex: 1000,
          background:
            "linear-gradient(180deg, rgba(15,15,15,0.9), rgba(15,15,15,0.6))",
          border: `1px solid ${COLORS.border}`,
          borderRadius: 2,
          pb: 2,
          pt: 2,
          mb: 3,
          backdropFilter: "blur(12px)",
          boxShadow: "0 20px 60px rgba(0,0,0,0.55)",
        }}
      >
        <Typography
          variant={isMobile ? "h5" : "h4"}
          fontWeight={900}
          fontFamily="Lexend"
          mb={1}
          sx={{
            color: COLORS.text,
            letterSpacing: "-0.01em",
            display: "inline-flex",
            alignItems: "center",
            gap: 1,
            px: { xs: 1, md: 2 },
            "&::after": {
              content: '""',
              width: 8,
              height: 8,
              borderRadius: "50%",
              backgroundColor: COLORS.red,
              boxShadow: "0 0 14px rgba(229,9,20,0.65)",
              display: "inline-block",
            },
          }}
        >
          My Dashboard
        </Typography>
        {!isGuestLite && (
          <Box sx={{ px: { xs: 1, md: 2 } }}>
            <SectionTabsUser value={activeTab} onChange={handleTabChange} />
          </Box>
        )}
      </Box>

      {/* Analytics */}
      {!isGuestLite && (
        <Box ref={analyticsRef} id="analytics" sx={sectionCardSx}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={12}>
              <PerfomanceSnapshots data={mockSnapshotData} columns={2} />
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Wallet overview */}
      {!isGuestLite && (
        <Box ref={walletRef} id="wallet" sx={sectionCardSx}>
          <Grid item xs={12} md={6} sx={{ pl: { md: 1 } }}>
            <ChipWalletOverview />
          </Grid>
        </Box>
      )}

      {/* Upcoming Sessions */}
      <Box ref={bookingsRef} id="bookings" sx={sectionCardSx}>
        <Typography
          variant="h6"
          fontWeight={800}
          mb={2}
          fontFamily="Lexend"
          sx={{ color: COLORS.text, letterSpacing: "-0.01em" }}
        >
          <Box component="span" sx={{ color: COLORS.red, mr: 1 }}>
            ●
          </Box>
          Upcoming Sessions
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <UpcomingSessionCard />
          </Grid>
        </Grid>
      </Box>

      {/* Recently Viewed */}
      {!isGuestLite && (
        <Box ref={upcomingRef} id="upcoming" sx={sectionCardSx}>
          <GroupedStageBookingsCarousel />
        </Box>
      )}

      {/* Favorite Talents */}
      {!isGuestLite && (
        <Box ref={favoriteRef} id="favorites" sx={sectionCardSx}>
          <SuitSelectorWithSection />
        </Box>
      )}

      {/* My deck */}
      {!isGuestLite && (
        <Box ref={myDeckRef} id="myDeck" sx={sectionCardSx}>
          <UserSelectedDecks fromAddToDeck={true} />
        </Box>
      )}

      {isGuestLite && (
        <Box sx={sectionCardSx}>
          <Typography sx={{ color: COLORS.muted, mb: 2 }}>
            You're currently using a guest lite profile. Update all your profile
            details to upgrade your account to get all the features.
          </Typography>
          <GradientButton
            onClick={() => navigate("/settings")}
            sx={{
              maxWidth: "220px",
              fontWeight: 800,
              borderRadius: 2,
              textTransform: "none",
              backgroundColor: COLORS.red,
              boxShadow: "0 12px 28px rgba(229,9,20,0.35)",
              "&:hover": { backgroundColor: COLORS.redHover },
            }}
          >
            Upgrade Profile
          </GradientButton>
        </Box>
      )}

      <GuestLiteModal
        open={isGuestLiteModalOpen}
        onClose={() => setIsGuestLiteModalOpen(false)}
      />
    </Box>
  );
};

export default UserDashboard_updated;
